import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

// ✅ Module-level (shared across ALL dialog-component instances)
// Kyunki ES module singleton hota hai, ye state sabhi instances ke beech share hoga
let openDialogCount = 0;
let savedScrollY = 0;

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

let startY = 0;

const recordTouchStart = (e) => {
  startY = e.touches[0].clientY;
};

const preventTouch = (e) => {
  const scroller = e.target.closest('.cart-drawer__items');
  if (!scroller) {
    e.preventDefault();
    return;
  }
  const { scrollTop, scrollHeight, clientHeight } = scroller;
  if (scrollHeight <= clientHeight) {
    e.preventDefault();
    return;
  }
  const currentY = e.touches[0].clientY;
  const goingDown = currentY > startY;
  const atTop = scrollTop <= 0;
  const atBottom = scrollTop + clientHeight >= scrollHeight - 1;
  if ((atTop && goingDown) || (atBottom && !goingDown)) {
    e.preventDefault();
  }
};

let lockedY = 0;
const handleViewport = () => {
  const vv = window.visualViewport;
  if (!vv) return;

  document.documentElement.style.setProperty('--vvh', `${vv.height}px`);
  window.scrollTo(0, lockedY);

  if (vv.height < window.innerHeight * 0.75) {
    showOverlay();
  } else {
    hideOverlay();
  }
};
let overlayEl = null;

const showOverlay = () => {
  if (overlayEl) return;
  overlayEl = document.createElement('div');
  overlayEl.className = 'ios-kb-overlay';
  document.body.appendChild(overlayEl);
};

const hideOverlay = () => {
  overlayEl?.remove();
  overlayEl = null;
};

// ✅ Body ko lock/unlock karne wale helpers — sirf iOS ke liye use honge.
// Android CSS ke `html:has(dialog[open]) { overflow: hidden }` se already
// handle ho raha hai, isliye JS-level fixed-positioning sirf iOS pe chalegi.
const lockBody = () => {
  if (!isIOS) return;
  requestAnimationFrame(() => {
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.overflow = 'hidden';
    document.body.style.top = `-${savedScrollY}px`;
  });
};

const unlockBody = () => {
  if (!isIOS) return;
  document.body.style.width = '';
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.overflow = '';
  window.scrollTo({ top: savedScrollY, behavior: 'instant' });
};

const addIOSTouchGuards = () => {
  if (!isIOS) return;
  document.addEventListener('touchstart', recordTouchStart, { passive: true });
  document.addEventListener('touchmove', preventTouch, { passive: false });
};

const removeIOSTouchGuards = () => {
  if (!isIOS) return;
  document.removeEventListener('touchstart', recordTouchStart);
  document.removeEventListener('touchmove', preventTouch);
};

// ✅ Global emergency reset — kisi bhi wajah se (Bogos re-render, animation
// hang, bfcache restore, koi bhi edge case) agar lock stuck reh jaye, isko
// kahin se bhi call karke turant sab kuch reset kiya ja sakta hai.
const forceResetAllLocks = () => {
  openDialogCount = 0;
  document.body.style.width = '';
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.overflow = '';
  removeIOSTouchGuards();
};

// ✅✅ SOURCE OF TRUTH FIX: Bogos jaise third-party apps humare showDialog()/
// closeDialog() methods ko bypass karke seedha dialog ko open/close kar
// sakte hain (apna khud ka fetch/AJAX + section re-render karke), ya poora
// cart-drawer HTML replace kar dete hain. Jab aisa hota hai, humara manual
// openDialogCount tracking real DOM state se "out of sync" ho jata hai —
// body lock laga rehta hai lekin use hatane wala code kabhi chalta nahi.
//
// Isliye ab body-lock ka decision manual counting se nahi, balki HAMESHA
// actual document me kitne `dialog[open]` maujood hain usi se hota hai.
// Chahe dialog kisi bhi tareeke se open/close ho (hamare code se, Bogos
// se, ya kisi bhi script se), ye observer turant sync kar dega.
let lockObserver = null;

const syncBodyLock = () => {
  const currentlyOpen = document.querySelectorAll('dialog[open]').length;

  if (currentlyOpen > 0 && openDialogCount === 0) {
    savedScrollY = window.scrollY;
    openDialogCount = currentlyOpen;
    lockBody();
    addIOSTouchGuards();
  } else if (currentlyOpen === 0 && openDialogCount > 0) {
    openDialogCount = 0;
    removeIOSTouchGuards();
    unlockBody();
  } else {
    openDialogCount = currentlyOpen;
  }
};

const startLockObserver = () => {
  if (lockObserver) return;
  lockObserver = new MutationObserver(() => {
    // Microtask jaisa turant chalta hai, animation flicker jaisa kuch nahi hoga
    syncBodyLock();
  });
  lockObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['open'],
    subtree: true,
    childList: true,
  });
};

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startLockObserver);
  } else {
    startLockObserver();
  }
}

export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }

    // ✅ Note: agar ye element (dialog open state me) DOM se remove ho
    // jaye — jaise Shopify section re-render ya Bogos ka apna re-render —
    // toh humein yahan manually kuch karne ki zaroorat nahi. MutationObserver
    // (syncBodyLock) `childList` changes bhi track karta hai, isliye agla
    // hi microtask me wo khud dekh lega ki DOM me ab `dialog[open]` count
    // kam ho gaya hai aur body lock ko sahi se sync/unlock kar dega.
    queueMicrotask(syncBodyLock);
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;
    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    this.refs.dialog.classList.remove('keyboard-open');

    requestAnimationFrame(() => {
      dialog.showModal();
      // ✅ Body lock ab MutationObserver (syncBodyLock) khud handle karta
      // hai jab dialog ka `open` attribute set hota hai — chahe showModal()
      // yahan se chale ya Bogos jaisi koi bahar ki script se.
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.style.animation = 'none';
    void dialog.offsetWidth;

    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    // ✅ CRITICAL FIX: Agar 'animationend' event kabhi fire na ho (jaise
    // Bogos bundle app close animation ke beech me hi drawer ka HTML
    // re-render kar de, ya style/class beech me hi badal jaye), toh
    // pehle wala plain `await onAnimationEnd(...)` HAMESHA KE LIYE latak
    // jata tha. Uske baad ke steps (touch guards remove, body unlock)
    // kabhi chalte hi nahi the — isi wajah se iOS pe "freeze" hota tha.
    // Promise.race se 500ms ke andar chahe animationend fire ho ya na ho,
    // hum guaranteed aage badhte hain.
    await Promise.race([
      onAnimationEnd(dialog, undefined, { subtree: false }),
      new Promise((resolve) => setTimeout(resolve, 500)),
    ]);

    dialog.close();
    // ✅ Body unlock ab MutationObserver (syncBodyLock) khud handle karta
    // hai jab dialog ka `open` attribute hatta hai.
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  #handleClick(event) {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeDialog();
  }

  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }
  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }
  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);

// ✅ bfcache fix: Checkout jaise pages pe navigate karke "back" button se
// wapas aane par (jaise Bogos bundle add-to-cart -> checkout -> back),
// iOS/Android browsers page ko bfcache (back-forward cache) se restore
// karte hain instead of fresh reload. Agar navigate karte waqt drawer
// open tha, toh body pe laga hua locked state waisa hi "frozen" restore
// ho jata hai. Isliye yahan explicitly reset karna padta hai.
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;

  // Restore hote hi jitne bhi dialog "open" dikh rahe hain sab close karo
  // (checkout se peeche aane par purana open drawer state stale hota hai),
  // fir observer khud lock ko sync kar dega. Extra safety ke liye ek
  // forceReset bhi kar dete hain taaki koi bhi leftover state na bache.
  document.querySelectorAll('dialog[open]').forEach((dialog) => {
    dialog.classList.remove('dialog-closing');
    dialog.close();
  });

  forceResetAllLocks();
  syncBodyLock();
});